/**
 * WixMart kauccja tandem — auto-add deposit (kauccja) line item when customer
 * adds a tandem-tagged product to cart. Mirrors PC-Market `Powiazanie` tandem
 * behavior on the cash register: every bottled drink purchase has a 0.50 zł
 * deposit listed as a separate line item (ZW VAT).
 *
 * Architecture: pure client-side because Shopify Cart Transform Function
 * `expand` operation cannot add price on non-Plus plans — it only displays
 * info-only sub-items. JS-based auto-add gives us a real chargeable line.
 *
 * Detection: we don't trust line properties (Shopify hides `_*` props from
 * /cart.js), so we fetch /products/<handle>.js for each non-deposit line and
 * check its tags. Results are cached in-memory for the page lifetime.
 *
 * Tag mapping (set on the main product via Office sync from Powiazanie):
 *   • `kaucja-pet-50gr`    → DEPOSIT_PET_VARIANT_ID    (0.50 zł plastic bottle)
 *   • `kaucja-puszka-50gr` → DEPOSIT_PUSZKA_VARIANT_ID (0.50 zł metal can)
 */
(function () {
  'use strict';

  const DEPOSIT_PET_VARIANT_ID    = 53750593061203;
  const DEPOSIT_PUSZKA_VARIANT_ID = 53750592897363;
  const DEPOSIT_VARIANT_IDS = [DEPOSIT_PET_VARIANT_ID, DEPOSIT_PUSZKA_VARIANT_ID];

  // Termobox (chłodzące opakowanie) — auto-dodawana pozycja 19 zł, gdy w koszyku
  // są mrożonki, wartość towarów < 300 zł (gratis od 300), a żaden towar nie ma
  // termoboksu wliczonego w cenę (mitka `termobox-w-cenie`). Obsługiwane przez
  // TEN SAM silnik co kaucja — żeby nie było drugiej pętli mutacji koszyka
  // (to właśnie "burza zapytań" psuła wcześniej checkout).
  const TERMOBOX_VARIANT_ID = 54104470323539;
  // Поріг рівно такий, як в усіх текстах сайту — 300 zł. До 27.08.2026 тут тихо
  // стояло 294 zł, щоб «Krewetki 5 kg» за 295 zł проходили gratis. Це давало
  // друге, невидиме число: смужка кошика вітала «Termobox gratis!» поруч із
  // написом «gratis od 300 zł». Тепер набори з термобоксом у ціні впізнаються
  // чесно — за міткою нижче, а поріг лишається один.
  const TERMOBOX_FREE_THRESHOLD_CENTS = 30000; // 300,00 zł — те саме число, що в текстах
  // Мітка товарів, у ціну яких термобокс уже входить (набори 5 кг з назвою
  // «+ Termobox gratis»). Такий товар не має діставати другий термобокс за 19 zł,
  // навіть коли кошик не дотягує до 300 zł.
  const TERMOBOX_INCLUDED_TAG = 'termobox-w-cenie';
  // Список товарів колекції 'mrozonki' живе у спільному assets/wxm-cart-collections.js
  const FEE_VARIANT_IDS = DEPOSIT_VARIANT_IDS.concat([TERMOBOX_VARIANT_ID]);

  // Debug logger — disabled by default in production. Включити з браузер-консолі:
  //   window.WXM_KAUCJA_DEBUG = true; location.reload();
  // Тоді всі DBG-рядки сиплються у консоль з префіксом [wxm-kaucja].
  const DBG = (...args) => {
    if (window.WXM_KAUCJA_DEBUG !== true) return;
    try { console.log('[wxm-kaucja]', ...args); } catch(e) {}
  };

  // Наш власний канал запитів, захоплений ДО того, як ми підмінили window.fetch
  // (нижче). Усі внутрішні виклики йдуть через нього, щоб НЕ спрацьовував наш
  // же перехоплювач на наших власних мутаціях кошика — інакше кожна наша
  // правка кошика ставила б у чергу зайвий прохід синхронізації.
  const NATIVE_FETCH = window.fetch.bind(window);

  // Усі внутрішні запити кошика — з таймаутом. Без нього завислий запит
  // (поганий мобільний зв'язок) лишав syncing=true до кінця життя сторінки:
  // сторож знімав блок кнопки, але кауція/термобокс більше не дораховувались —
  // замовлення могло піти без застави. AbortController гарантує, що await
  // завершиться (успіхом чи відмовою) і блок finally у syncOnce завжди спрацює.
  const FETCH_TIMEOUT_MS = 8000;
  function timedFetch(url, opts) {
    const ctrl = new AbortController();
    const t = setTimeout(function () { ctrl.abort(); }, FETCH_TIMEOUT_MS);
    const merged = Object.assign({}, opts, { signal: ctrl.signal });
    return NATIVE_FETCH(url, merged).finally(function () { clearTimeout(t); });
  }

  // Page-lifetime cache: handle → 'pet' | 'puszka' | null
  const tagCache = new Map();

  let syncing = false;
  let syncQueued = false;
  let syncTimer = null;

  // Скільки разів пробувати, коли дані правил (список заморозки чи мітки) не прочитались.
  let retryAttempts = 0;
  const MAX_RETRIES = 3;

  /**
   * Стан «кошик перераховується». Поки true — кнопку «Оформити» треба
   * блокувати: інакше клієнт відправляє форму, поки термобокс/кауція ще в
   * дорозі, і замовлення йде без платної позиції (або Shopify викидає його
   * назад через «кошик змінився»).
   */
  let busyWatchdog = null;
  const BUSY_MAX_MS = 6000;

  function setBusy(value) {
    window.wxmKaucjaBusy = value;
    clearTimeout(busyWatchdog);
    if (value) {
      // ⚠️ ЗАПОБІЖНИК: стан «зайнято» НІКОЛИ не має зависнути. Заблокована
      // назавжди кнопка «Оформити» — гірше за будь-яку проблему, яку ми тут
      // лікуємо. Якщо синхронізація не завершилась за 6 с — розблоковуємо.
      busyWatchdog = setTimeout(function () {
        if (window.wxmKaucjaBusy) {
          DBG('watchdog: примусово знімаю «зайнято» після ' + BUSY_MAX_MS + ' мс');
          setBusy(false);
        }
      }, BUSY_MAX_MS);
    }
    document.dispatchEvent(new CustomEvent('wxm:cart-busy', { detail: { busy: value } }));
  }
  window.wxmKaucjaBusy = false;

  async function fetchCart() {
    const r = await timedFetch('/cart.js', { headers: { 'Accept': 'application/json' }});
    return r.json();
  }

  function isDepositLine(item) {
    return DEPOSIT_VARIANT_IDS.includes(Number(item.variant_id));
  }

  /**
   * Тип застави для товару: 'pet' | 'puszka' | 'none' | null.
   *
   * ⚠️ 'none' («мітки прочитали, застави нема») і null («мітки НЕ прочитали») —
   * це РІЗНІ речі, їх не можна плутати. Раніше обидва випадки давали null і
   * невдалий запит /products/<handle>.js мовчки означав «застава не потрібна»
   * → вже додана застава ВИДАЛЯЛАСЬ із кошика. Та сама хвороба, що була в
   * термобокса зі списком заморозки.
   *
   * Мітки беремо зі спільного кешу — той самий запит обслуговує і плашку 18+.
   */
  async function classifyHandle(handle) {
    if (!handle) return 'none';
    if (tagCache.has(handle)) return tagCache.get(handle);

    if (!(window.wxmCartCollections && window.wxmCartCollections.productTags)) {
      return null; // спільний модуль недоступний — «не знаю»
    }

    const tags = await window.wxmCartCollections.productTags(handle);
    if (tags === null) return null; // не кешуємо — наступний раз спробуємо знову

    let result = 'none';
    if (tags.includes('kaucja-pet-50gr')) result = 'pet';
    else if (tags.includes('kaucja-puszka-50gr')) result = 'puszka';
    tagCache.set(handle, result);
    return result;
  }

  /**
   * Скільки застав треба. Повертає { required, unknown }.
   * unknown=true — хоч по одній позиції мітки не прочитались, отже чіпати
   * застави НЕ можна (див. коментар до classifyHandle).
   */
  async function computeRequiredDeposits(cart) {
    const required = {
      [DEPOSIT_PET_VARIANT_ID]: 0,
      [DEPOSIT_PUSZKA_VARIANT_ID]: 0,
    };
    let unknown = false;
    const items = cart.items || [];
    // Parallel classify all non-deposit lines
    const checks = items
      .filter(it => !isFeeLine(it))
      .map(async (it) => {
        const kind = await classifyHandle(it.handle);
        if (kind === null) { unknown = true; return; }
        if (kind === 'pet')    required[DEPOSIT_PET_VARIANT_ID]    += it.quantity;
        if (kind === 'puszka') required[DEPOSIT_PUSZKA_VARIANT_ID] += it.quantity;
      });
    await Promise.all(checks);
    return { required, unknown };
  }

  function existingDeposits(cart) {
    const found = {};
    for (const item of cart.items || []) {
      if (!isDepositLine(item)) continue;
      const vid = Number(item.variant_id);
      if (!found[vid]) {
        found[vid] = { key: item.key, quantity: item.quantity };
      }
    }
    return found;
  }

  // ── Termobox helpers ──────────────────────────────────────────────────────
  function isFeeLine(item) {
    return FEE_VARIANT_IDS.includes(Number(item.variant_id));
  }

  // Frozen product handles — спільне джерело для теми (assets/wxm-cart-collections.js).
  // Повертає { handle: true } АБО null, якщо список не завантажився.
  // ⚠️ null = «не знаю». Раніше тут був `.catch(() => ({}))`, який кешував
  // ПОРОЖНІЙ список на все життя сторінки → термобокс не додавався, а вже
  // доданий — видалявся. Див. коментар у wxm-cart-collections.js.
  function getFrozenHandles() {
    if (window.wxmCartCollections && window.wxmCartCollections.frozen) {
      return window.wxmCartCollections.frozen();
    }
    DBG('wxmCartCollections недоступний — термобокс цього разу не чіпаємо');
    return Promise.resolve(null);
  }

  // Wartość towarów w groszach, BEZ pozycji opłat (kaucja + termobox).
  function goodsSubtotalCents(cart) {
    let sum = 0;
    for (const it of cart.items || []) {
      if (isFeeLine(it)) continue;
      sum += (it.final_line_price || it.line_price || 0);
    }
    return sum;
  }

  /**
   * Чи є в кошику товар, у ціні якого термобокс уже врахований (мітка
   * `termobox-w-cenie`). true | false | null.
   *
   * ⚠️ null = «мітки не прочитались», а НЕ «мітки нема» — той самий принцип,
   * що для списку заморозки і для застави. При null термобокс не чіпаємо.
   * Мітки беруться зі спільного кешу wxm-cart-collections.js — тобто цей
   * перегляд не додає жодного запиту понад ті, що вже робить застава і 18+.
   */
  async function cartHasTermoboxIncluded(cart) {
    if (!(window.wxmCartCollections && window.wxmCartCollections.productTags)) return null;
    for (const it of cart.items || []) {
      if (isFeeLine(it)) continue;
      if (!it.handle) continue;
      const tags = await window.wxmCartCollections.productTags(it.handle);
      if (tags === null) return null;
      if (tags.includes(TERMOBOX_INCLUDED_TAG)) return true;
    }
    return false;
  }

  function cartHasFrozen(cart, frozenSet) {
    for (const it of cart.items || []) {
      if (isFeeLine(it)) continue;
      if (frozenSet[it.handle]) return true;
    }
    return false;
  }

  function findLine(cart, variantId) {
    for (const item of cart.items || []) {
      if (Number(item.variant_id) === variantId) return { key: item.key, quantity: item.quantity };
    }
    return null;
  }

  /**
   * Get the section IDs Dawn cart-drawer uses for re-render, in the exact
   * shape it expects (parsedState.sections[section.id] = HTML).
   */
  function getDrawerSectionIds() {
    const drawer = document.querySelector('cart-drawer');
    if (drawer && typeof drawer.getSectionsToRender === 'function') {
      return drawer.getSectionsToRender().map(s => s.id).join(',');
    }
    return 'cart-drawer,cart-icon-bubble';
  }

  /**
   * Mutate cart and return the response.sections payload (when requested via
   * `sections` form-data param — Shopify embeds rendered HTML in the response,
   * same pattern Dawn product-form uses internally).
   */
  async function applyDelta(adds, updates) {
    const sectionsParam = getDrawerSectionIds();
    const sectionsUrl   = window.location.pathname;
    let lastSections = null;

    if (Object.keys(updates).length) {
      const body = JSON.stringify({ updates, sections: sectionsParam, sections_url: sectionsUrl });
      const r = await timedFetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body,
      });
      try { const j = await r.json(); if (j && j.sections) lastSections = j.sections; } catch(e) {}
    }
    for (const a of adds) {
      const body = JSON.stringify({ id: a.id, quantity: a.quantity, sections: sectionsParam, sections_url: sectionsUrl });
      const r = await timedFetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body,
      });
      try { const j = await r.json(); if (j && j.sections) lastSections = j.sections; } catch(e) {}
    }
    return lastSections;
  }

  async function syncOnce() {
    if (syncing) {
      DBG('sync skipped — already running');
      syncQueued = true;
      return;
    }
    syncing = true;
    let retryLater = false;
    DBG('--- syncOnce start ---');

    try {
      const cart = await fetchCart();

      // Порожній кошик — жодне правило (кауція/термобокс) не застосовне.
      // Виходимо ДО завантаження списків колекцій: інакше кожне гортання
      // каталогу тягнуло ~230 KB JSON у вікні вимірювання LCP.
      if (!(cart.items || []).length) {
        DBG('кошик порожній — нічого робити');
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { source: 'wxm-kaucja', cart } }));
        return;
      }
      DBG('cart fetched, items=' + (cart.items || []).length, (cart.items || []).map(i => ({handle: i.handle, qty: i.quantity, vid: i.variant_id})));
      const { required, unknown: depositsUnknown } = await computeRequiredDeposits(cart);
      DBG('required deposits:', required, 'unknown=' + depositsUnknown);
      const have     = existingDeposits(cart);
      DBG('existing deposits:', have);

      const adds = [];
      const updates = {};

      if (depositsUnknown) {
        // Мітки хоч однієї позиції не прочитались — не чіпаємо застави взагалі,
        // інакше ризикуємо зняти правильну заставу. Спробуємо ще раз.
        DBG('застави пропущено — мітки невідомі; повторимо');
        retryLater = true;
      } else {
        for (const vid of DEPOSIT_VARIANT_IDS) {
          const want = required[vid] || 0;
          const cur  = have[vid];
          if (want === 0 && !cur) continue;
          if (want === 0 && cur) { updates[cur.key] = 0; continue; }
          // Нема рядка — ставимо АБСОЛЮТНУ кількість за id варіанта через
          // /cart/update.js (Shopify сам додає позицію, якої нема). Не /cart/add.js:
          // той ДОДАЄ до наявної кількості, і два проходи впритул (обірваний
          // запит, повільний зв'язок, дві вкладки) давали 2 застави чи 2 термобокси
          // (#5498, #5350 — 07.09.2026).
          if (!cur)              { updates[String(vid)] = want; continue; }
          if (cur.quantity !== want) updates[cur.key] = want;
        }
      }

      // ── Termobox: mrożonki w koszyku + towary < 300 zł → 1 szt, inaczej 0 ──
      //
      // ⚠️ frozenSet === null означає «список заморозки не завантажився», а НЕ
      // «заморозки в кошику нема». Різниця коштувала замовлень: при null стара
      // версія рахувала wantTermo=0 і ВИДАЛЯЛА вже доданий термобокс — клієнт
      // отримував заморозку без термобокса. При null не чіпаємо термобокс
      // взагалі і плануємо ще одну спробу.
      const frozenSet = await getFrozenHandles();
      const termoIncluded = await cartHasTermoboxIncluded(cart);
      const curTermo = findLine(cart, TERMOBOX_VARIANT_ID);
      if (frozenSet === null || termoIncluded === null) {
        DBG('termobox пропущено — список заморозки або мітки невідомі; повторимо');
        retryLater = true;
      } else {
        const goods = goodsSubtotalCents(cart);
        const wantTermo = (!termoIncluded && cartHasFrozen(cart, frozenSet) && goods < TERMOBOX_FREE_THRESHOLD_CENTS) ? 1 : 0;
        DBG('termobox want=' + wantTermo, 'cur=', curTermo, 'goods=' + goods, 'wCenie=' + termoIncluded);
        if (wantTermo === 0 && curTermo) updates[curTermo.key] = 0;
        else if (wantTermo === 1 && !curTermo) updates[String(TERMOBOX_VARIANT_ID)] = 1; // абсолютно, не «додати»
        else if (wantTermo === 1 && curTermo && curTermo.quantity !== 1) updates[curTermo.key] = 1;
      }

      DBG('plan: adds=', adds, 'updates=', updates);
      const changed = adds.length || Object.keys(updates).length;
      if (changed) {
        // Кнопку «Оформити» глушимо ЛИШЕ на реальний час зміни кошика, а не на
        // весь час діагностики вище (fetch /cart.js + мітки + список заморозки).
        // Раніше setBusy(true) стояв на початку syncOnce і робив кнопку сірою на
        // 1–6 с при КОЖНОМУ відкритті кошика — половина клієнтів встигала клікнути
        // в це «мертве вікно» і йшла (злив кошик → оформлення).
        setBusy(true);
        DBG('applying delta');
        const sections = await applyDelta(adds, updates);
        DBG('sections received:', sections ? Object.keys(sections) : 'none');

        // CRITICAL: do NOT call drawer.renderContents() — Dawn's implementation
        // (assets/cart-drawer.js:86-89) always calls `this.open()` at the end,
        // which jacks the user's browse on desktop. We replicate the section
        // swap logic manually, preserving the drawer's open/closed state.
        const drawer = document.querySelector('cart-drawer');
        if (sections) {
          try {
            const drawerInner = drawer ? drawer.querySelector('.drawer__inner') : null;
            if (drawerInner && drawerInner.classList.contains('is-empty')) {
              drawerInner.classList.remove('is-empty');
            }
            const sectionsToRender = (drawer && typeof drawer.getSectionsToRender === 'function')
              ? drawer.getSectionsToRender()
              : [
                  { id: 'cart-drawer', selector: '#CartDrawer' },
                  { id: 'cart-icon-bubble' },
                ];
            sectionsToRender.forEach((section) => {
              const sectionElement = section.selector
                ? document.querySelector(section.selector)
                : document.getElementById(section.id);
              if (!sectionElement) return;
              const html = sections[section.id];
              if (!html) return;
              const parsed = new DOMParser().parseFromString(html, 'text/html');
              const target = parsed.querySelector(section.selector || '.shopify-section');
              if (target) {
                sectionElement.innerHTML = target.innerHTML;
              }
            });
            DBG('cart sections swapped (drawer state preserved)');
          } catch (e) {
            DBG('manual section swap threw', e);
          }
        }
      }
      if (changed) {
        // Сторінка /cart (не шухляда) — це форма з полями updates[] на кожен рядок.
        // Після нашої правки вона лишалась зі старими числами, а кнопка «Оформити»
        // відправляла їх назад у Shopify: кауція 2 при 3 пляшках (#5495), термобокс
        // ×2 (#5498). Просимо Dawn перемалювати кошик і шухляду з сервера
        // (CartItems.onCartUpdate). Наш власний підписник це джерело ігнорує.
        // ⚠️ PUB_SUB_EVENTS у Dawn — const, не властивість window: лише typeof.
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
          try { publish(PUB_SUB_EVENTS.cartUpdate, { source: 'wxm-kaucja' }); } catch (e) { DBG('publish failed', e); }
        }
      }
      // Always fire cart:updated so listeners (free-shipping bar, frozen bar,
      // header counter) refresh even when kaucja itself made no changes —
      // covers direct cart mutations (+/− in drawer) where Dawn updated DOM
      // but our bars need re-render against the new totals.
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { source: 'wxm-kaucja' } }));
    } catch (err) {
      console.warn('[wxm-kaucja] sync error:', err);
    } finally {
      syncing = false;
      const again = syncQueued;
      if (!again) setBusy(false); // є черга — «зайнято» тримаємо до її проходу
      // Успішний прохід (усі правила прочитались) — скидаємо лічильник спроб,
      // інакше 3 разові збої за весь візит вимикали повтор назавжди.
      if (!retryLater) retryAttempts = 0;
      if (again) {
        syncQueued = false;
        scheduleSync({ mutation: true });
      } else if (retryLater && retryAttempts < MAX_RETRIES) {
        // Дані правил не прочитались — пробуємо ще раз із паузою.
        // Обмежені спроби: краще без термобокса, ніж нескінченний цикл запитів.
        retryAttempts += 1;
        DBG('повтор: дані правил невідомі, спроба ' + retryAttempts);
        setTimeout(scheduleSync, 800 * retryAttempts);
      }
    }
  }

  // ⚠️ НЕ «пропускати», якщо синхронізація вже йде — СТАВИТИ В ЧЕРГУ.
  // Було: усі виклики стояли за перевіркою `if (!syncing)`, тож будь-яка зміна
  // кошика під час активної синхронізації ТИХО ГУБИЛАСЬ. Саме тому термобокс
  // не з'являвся назад, коли клієнт зменшував кількість (сума падала нижче
  // 294 zł, але перерахунок не запускався). Тепер зміна не губиться ніколи.
  // opts.mutation — виклик через ЗМІНУ кошика (не перший прохід при завантаженні
  // і не повтор правил): одразу «зайнято», щоб «Оформити» не пішло зі старими
  // доплатами у вікні між «+» на товарі й дорахунком застави/термобокса.
  // Раніше «зайнято» вмикалось лише на сам запис — вікно в 1–2 с лишалось.
  function scheduleSync(opts) {
    if (opts && opts.mutation) setBusy(true);
    if (syncing) {
      syncQueued = true;
      return;
    }
    clearTimeout(syncTimer);
    syncTimer = setTimeout(syncOnce, 200);
  }

  // ---------------------------------------------------------------------------
  // Detection strategy 1 — Dawn's native pubsub (most reliable; defer-safe)
  // ---------------------------------------------------------------------------
  // Dawn product-form.js publishes PUB_SUB_EVENTS.cartUpdate after every
  // /cart/add call. We `subscribe` and trigger our sync. This avoids the race
  // condition of fetch-override (which fails if product-form.js loads first
  // and captures the pre-override window.fetch reference).
  function trySubscribePubSub() {
    if (window.subscribe && window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.cartUpdate) {
      DBG('subscribing to PUB_SUB_EVENTS.cartUpdate');
      window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, (payload) => {
        if (payload && payload.source === 'wxm-kaucja') return; // ignore self-emitted
        DBG('pubsub cartUpdate received', payload);
        scheduleSync({ mutation: true });
      });
      return true;
    }
    return false;
  }

  // Subscribe ASAP, but if pubsub not yet defined, retry on DOMContentLoaded.
  if (!trySubscribePubSub()) {
    document.addEventListener('DOMContentLoaded', () => {
      if (!trySubscribePubSub()) {
        DBG('pubsub still unavailable on DOMContentLoaded — fallback to fetch override');
      }
    });
  }

  // ---------------------------------------------------------------------------
  // Detection strategy 2 — fetch override (backup for non-Dawn calls)
  // ---------------------------------------------------------------------------
  const _fetch = window.fetch;
  window.fetch = function (resource, init) {
    const url = (typeof resource === 'string') ? resource : (resource && resource.url) || '';
    const isCartMutation = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/.test(url);
    const promise = _fetch.call(this, resource, init);
    // Без `!syncing`: наші власні мутації сюди не потрапляють (вони йдуть через
    // NATIVE_FETCH), а чужу зміну кошика губити не можна — scheduleSync() сам
    // поставить її в чергу, якщо саме зараз іде синхронізація.
    if (isCartMutation) {
      DBG('fetch interceptor — cart mutation detected:', url);
      setBusy(true); // від початку чужого запиту до кінця нашого дорахунку
      promise.then(() => scheduleSync({ mutation: true })).catch(() => { setBusy(false); });
    }
    return promise;
  };

  // ---------------------------------------------------------------------------
  // Detection strategy 3 — DOM event listeners (other themes/sections)
  // ---------------------------------------------------------------------------
  // ⚠️ Свою власну подію ігноруємо — інакше syncOnce() (який наприкінці сам
  // розсилає cart:updated) нескінченно перезапускав би себе. Такий самий захист
  // за джерелом уже стоїть у підписці на pubsub вище.
  const isSelfEvent = (e) => !!(e && e.detail && e.detail.source === 'wxm-kaucja');

  document.addEventListener('cart:added',  () => { DBG('event cart:added');  scheduleSync({ mutation: true }); });
  document.addEventListener('cart:updated', (e) => {
    if (isSelfEvent(e)) return;
    DBG('event cart:updated');
    scheduleSync({ mutation: true });
  });
  document.addEventListener('cart-update',  () => { DBG('event cart-update');  scheduleSync({ mutation: true }); });

  // ---------------------------------------------------------------------------
  // Initial sync — covers session restore (cart already has tagged items)
  // ---------------------------------------------------------------------------
  if (document.readyState !== 'loading') scheduleSync();
  else document.addEventListener('DOMContentLoaded', scheduleSync);
})();
